import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, BookOpen, Check, Copy, Image as ImageIcon, LineChart, MessageSquare, Video } from 'lucide-react';
import { CodeBlock } from './CodeBlock';
import {
  loadCatalog,
  formatContext,
  formatPrice,
  modelTitle,
  capabilities,
  type CatalogModel,
  type ProviderDetail,
} from './modelCatalog';
import { IMAGE_DEMOS, type ImageDemo } from './imageDemos';
import { VIDEO_DEMOS, type VideoDemo } from './videoDemos';

type MediaLanguage = 'python' | 'javascript' | 'curl';

const Fact: React.FC<{ label: string; value: string; code?: boolean }> = ({ label, value, code }) => (
  <div className="min-w-0 rounded-xl border border-white/10 bg-white/[0.02] p-5">
    <div className="mb-2 text-xs font-mono uppercase tracking-[0.18em] text-white/35">{label}</div>
    {code ? (
      <code className="block truncate text-sm text-white/80">{value}</code>
    ) : (
      <div className="truncate text-lg font-semibold tracking-tight">{value}</div>
    )}
  </div>
);

const snippet = (id: string) =>
  `from openai import OpenAI

client = OpenAI(
    api_key="app-...",            # app.nz gateway key
    base_url="https://app.nz/v1",
)

resp = client.chat.completions.create(
    model="${id}",
    messages=[{"role": "user", "content": "say hi"}],
)
print(resp.choices[0].message.content)`;

const API_BASE = 'https://app.nz/v1';
const API_KEY = 'app-...';

function isForecastModel(m: CatalogModel): boolean {
  if (m.modality === 'forecast') return true;
  if ((m.price_per_forecast ?? 0) > 0) return true;
  return /(chronos|forecast|time-series|timeseries)/i.test(m.id);
}

// Forecast models speak a forecasting API, not chat.completions. Give a correct,
// copy-pasteable /api/forecast example instead of a misleading chat snippet.
function forecastSnippet(lang: MediaLanguage): string {
  if (lang === 'python') {
    return `import requests

r = requests.post(
    "https://app.nz/api/forecast",
    headers={"Authorization": "Bearer ${API_KEY}"},
    json={
        "values": [100, 102, 99, 105, 110, 108, 112, 120],   # your history
        "prediction_length": 12,                              # steps ahead
        "quantile_levels": [0.1, 0.5, 0.9],
        # optional — paste extra columns for multivariate forecasting:
        # "past_covariates": {"price": [...], "ad_spend": [...]},
    },
).json()

print(r["forecast"]["mean"])                 # median forecast per step
print(r["forecast"]["quantiles"]["0.9"])     # upper band`;
  }
  if (lang === 'javascript') {
    return `const r = await fetch("https://app.nz/api/forecast", {
  method: "POST",
  headers: {
    "Authorization": "Bearer ${API_KEY}",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    values: [100, 102, 99, 105, 110, 108, 112, 120],
    prediction_length: 12,
    quantile_levels: [0.1, 0.5, 0.9],
    // optional multivariate covariates:
    // past_covariates: { price: [...], ad_spend: [...] },
  }),
}).then((res) => res.json());

console.log(r.forecast.mean);`;
  }
  return `curl https://app.nz/api/forecast \\
  -H "Authorization: Bearer ${API_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "values": [100, 102, 99, 105, 110, 108, 112, 120],
    "prediction_length": 12,
    "quantile_levels": [0.1, 0.5, 0.9]
  }'`;
}

// modelFacts returns the spec tiles that actually apply to a model's modality.
// Image/video/media models have no token context or per-1M pricing, so showing
// those as "N/A" reads like broken docs — surface price-per-output + sizes.
function modelFacts(model: CatalogModel, modality: string): { label: string; value: string }[] {
  const usd = (v?: number) => (v == null ? 'N/A' : v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`);
  if (modality === 'image') {
    return [
      { label: 'Price / image', value: usd(model.price_per_image) },
      { label: 'Sizes', value: model.supported_sizes?.length ? model.supported_sizes.slice(0, 4).join(', ') : 'flexible' },
    ];
  }
  if (modality === 'video') {
    const price = model.price_per_video != null ? `${usd(model.price_per_video)} / video` : model.price_per_second != null ? `${usd(model.price_per_second)} / sec` : 'N/A';
    return [{ label: 'Price', value: price }, { label: 'Output', value: 'video' }];
  }
  if (modality === 'forecast') {
    return [{ label: 'Price / forecast', value: usd(model.price_per_forecast) }, { label: 'Context', value: `${formatContext(model.context_window)} steps` }];
  }
  if (modality === 'embeddings') {
    return [{ label: 'Input price', value: `${formatPrice(model.input_price_per_1m)} / 1M` }, { label: 'Context', value: `${formatContext(model.context_window)} tokens` }];
  }
  if (modality === 'speech' || modality === 'transcription') {
    const price = model.price_per_minute != null ? `${usd(model.price_per_minute)} / min` : `${formatPrice(model.input_price_per_1m)} / 1M`;
    return [{ label: 'Price', value: price }, { label: 'Output', value: modality === 'speech' ? 'audio' : 'text' }];
  }
  // Chat / text models.
  return [
    { label: 'Context', value: `${formatContext(model.context_window)} tokens` },
    { label: 'Max output', value: model.max_output_tokens ? `${formatContext(model.max_output_tokens)} tokens` : 'N/A' },
    { label: 'Input price', value: `${formatPrice(model.input_price_per_1m)} / 1M` },
    { label: 'Output price', value: `${formatPrice(model.output_price_per_1m)} / 1M` },
  ];
}

function isVideoModel(id: string): boolean {
  if (VIDEO_DEMOS[id]) return true;
  return /(text-to-video|image-to-video|reference-to-video|lipsync|\bvideo\b)/i.test(id);
}

function buildVideoPayload(modelId: string, demo?: VideoDemo) {
  const isHappyHorse = modelId === 'alibaba/happy-horse/image-to-video';
  const payload: Record<string, unknown> = {
    model: modelId,
    prompt: demo?.prompt || 'A cinematic product demo shot of a compact AI routing console, slow camera push-in, clean studio lighting, no readable text.',
    resolution: demo?.resolution || '720p',
    duration: isHappyHorse ? Number(demo?.duration || 5) : demo?.duration || '4',
    aspect_ratio: demo?.aspectRatio || '16:9',
  };
  if (isHappyHorse) {
    payload.enable_safety_checker = true;
  } else {
    payload.generate_audio = demo?.generateAudio ?? false;
  }
  if (demo?.imageUrl) payload.image_url = demo.imageUrl;
  if (demo?.endImageUrl) payload.end_image_url = demo.endImageUrl;
  if (demo?.imageUrls?.length) payload.image_urls = demo.imageUrls;
  if (demo?.videoUrls?.length) payload.video_urls = demo.videoUrls;
  if (demo?.audioUrls?.length) payload.audio_urls = demo.audioUrls;
  return payload;
}

function generateVideoSnippet(modelId: string, demo: VideoDemo | undefined, lang: MediaLanguage) {
  const payload = buildVideoPayload(modelId, demo);
  const body = JSON.stringify(payload, null, 2);

  if (lang === 'python') {
    return `from openai import OpenAI

client = OpenAI(
    api_key="${API_KEY}",
    base_url="${API_BASE}",
)

result = client.post(
    "/videos/generations",
    body=${JSON.stringify(payload, null, 4)},
    cast_to=dict,
)

print(result["video_url"])`;
  }

  if (lang === 'javascript') {
    return `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: "${API_KEY}",
  baseURL: "${API_BASE}",
});

const result = await client.post("/videos/generations", {
  body: ${body},
  cast_to: Object,
});

console.log(result.video_url);`;
  }

  return `curl "${API_BASE}/videos/generations" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${API_KEY}" \\
  -d @- <<'JSON'
${body}
JSON`;
}

function generateImageSnippet(demo: ImageDemo, lang: MediaLanguage) {
  const payload = demo.payload;
  const body = JSON.stringify(payload, null, 2);

  if (lang === 'python') {
    return `from openai import OpenAI

client = OpenAI(
    api_key="${API_KEY}",
    base_url="${API_BASE}",
)

result = client.post(
    "/images/generations",
    body=${JSON.stringify(payload, null, 4)},
    cast_to=dict,
)

print(result["data"][0]["url"])`;
  }

  if (lang === 'javascript') {
    return `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: "${API_KEY}",
  baseURL: "${API_BASE}",
});

const result = await client.post("/images/generations", {
  body: ${body},
  cast_to: Object,
});

console.log(result.data[0].url);`;
  }

  return `curl "${API_BASE}/images/generations" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${API_KEY}" \\
  -d @- <<'JSON'
${body}
JSON`;
}

export const ModelPage: React.FC<{ modelId: string }> = ({ modelId }) => {
  const decodedId = useMemo(() => {
    try {
      return decodeURIComponent(modelId);
    } catch {
      return modelId;
    }
  }, [modelId]);

  const [model, setModel] = useState<CatalogModel | null>(null);
  const [provider, setProvider] = useState<ProviderDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'missing' | 'error'>('loading');
  const [mediaLang, setMediaLang] = useState<MediaLanguage>('python');
  const [mediaCopied, setMediaCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    loadCatalog()
      .then((data) => {
        if (cancelled) return;
        const found = data.models.find((m) => m.id === decodedId);
        if (!found) {
          setState('missing');
          return;
        }
        setModel(found);
        setProvider(data.providerDetails.find((p) => p.name.toLowerCase() === found.provider.toLowerCase()) ?? null);
        setState('ok');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [decodedId]);

  const modality = model?.modality ?? '';
  const isVideo = useMemo(() => (model ? modality === 'video' || isVideoModel(model.id) : false), [model, modality]);
  const isForecast = useMemo(() => (model ? isForecastModel(model) : false), [model]);
  const videoDemo = model ? VIDEO_DEMOS[model.id] : undefined;
  const imageDemo = model ? IMAGE_DEMOS[model.id] : undefined;
  const isImage = modality === 'image' || !!imageDemo;
  const isMedia = isVideo || isImage || isForecast;

  const fcastSnippet = useMemo(() => (isForecast ? forecastSnippet(mediaLang) : ''), [isForecast, mediaLang]);

  const videoSnippet = useMemo(
    () => (model && isVideo ? generateVideoSnippet(model.id, videoDemo, mediaLang) : ''),
    [model, isVideo, videoDemo, mediaLang],
  );
  const imageSnippet = useMemo(
    () => (imageDemo ? generateImageSnippet(imageDemo, mediaLang) : ''),
    [imageDemo, mediaLang],
  );

  const copyMedia = async () => {
    await navigator.clipboard.writeText(isForecast ? fcastSnippet : isVideo ? videoSnippet : imageSnippet);
    setMediaCopied(true);
    window.setTimeout(() => setMediaCopied(false), 1600);
  };

  if (state === 'loading') {
    return (
      <main className="mx-auto max-w-5xl px-6 pb-24 pt-28">
        <div className="h-8 w-40 animate-pulse rounded bg-white/10" />
        <div className="mt-6 h-16 w-2/3 animate-pulse rounded bg-white/10" />
      </main>
    );
  }

  if (state !== 'ok' || !model) {
    return (
      <main className="mx-auto max-w-5xl px-6 pb-24 pt-28">
        <a href="/models" className="inline-flex items-center gap-1.5 text-xs font-mono text-white/50 hover:text-white">
          <ArrowLeft className="h-3.5 w-3.5" /> Model directory
        </a>
        <h1 className="mt-8 text-3xl font-bold tracking-tight">
          {state === 'missing' ? `No model “${decodedId}”` : 'Catalogue unavailable'}
        </h1>
        <p className="mt-3 text-white/55">
          {state === 'missing'
            ? 'That model id is not in the gateway catalogue. Browse the directory for available models.'
            : 'Could not reach the model catalogue. Try again shortly.'}
        </p>
      </main>
    );
  }

  const providerLabel = provider?.displayName ?? model.provider;
  const name = modelTitle(model.id);

  return (
    <main className="mx-auto max-w-5xl px-6 pb-24 pt-28">
      <a href="/models" className="inline-flex items-center gap-1.5 text-xs font-mono text-white/50 hover:text-white">
        <ArrowLeft className="h-3.5 w-3.5" /> Model directory
      </a>

      <div className="mb-10 mt-8">
        <div className="mb-4 flex items-center gap-2 text-xs font-mono text-white/45">
          {provider?.logo ? <img src={provider.logo} alt="" className="h-5 w-5 rounded-sm object-contain" /> : null}
          <a
            href={`/models?provider=${encodeURIComponent(model.provider.toLowerCase())}`}
            className="underline decoration-white/20 underline-offset-4 hover:text-white"
          >
            {providerLabel}
          </a>
        </div>
        <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl">{name}</h1>
        <div className="flex flex-wrap gap-2">
          {capabilities(model).map((c) => (
            <span key={c} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-mono text-white/65">
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="mb-10 grid gap-4 md:grid-cols-3">
        <Fact label="Model ID" value={model.id} code />
        {modelFacts(model, modality).map((f) => (
          <Fact key={f.label} label={f.label} value={f.value} />
        ))}
        <Fact label="Provider" value={providerLabel} />
      </div>

      {model.aliases?.length ? (
        <div className="mb-10 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h2 className="mb-3 text-sm font-mono uppercase tracking-[0.18em] text-white/35">Aliases</h2>
          <div className="flex flex-wrap gap-2">
            {model.aliases.map((a) => (
              <code key={a} className="rounded border border-white/10 bg-white/[0.04] px-2 py-1 text-xs text-white/70">
                {a}
              </code>
            ))}
          </div>
        </div>
      ) : null}

      {!isMedia && (
        <div className="mb-10 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h2 className="mb-4 text-xl font-bold tracking-tight">Call it through the gateway</h2>
          <CodeBlock code={snippet(model.id)} language="python" />
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        {isForecast ? (
          <a
            href="/time-series-forecasting"
            className="inline-flex items-center justify-center gap-2 rounded border border-white bg-sky-400 px-5 py-3 font-mono text-sm font-bold text-black transition-colors hover:bg-sky-300"
          >
            <LineChart className="h-4 w-4" /> Open forecaster
          </a>
        ) : isVideo ? (
          <a
            href={`/playground?model=${encodeURIComponent(model.id)}&mode=video`}
            className="inline-flex items-center justify-center gap-2 rounded border border-white bg-sky-400 px-5 py-3 font-mono text-sm font-bold text-black transition-colors hover:bg-sky-300"
          >
            <Video className="h-4 w-4" /> Generate video
          </a>
        ) : isImage ? (
          <a
            href={`/playground?model=${encodeURIComponent(model.id)}&mode=image`}
            className="inline-flex items-center justify-center gap-2 rounded border border-white bg-sky-400 px-5 py-3 font-mono text-sm font-bold text-black transition-colors hover:bg-sky-300"
          >
            <ImageIcon className="h-4 w-4" /> Generate image
          </a>
        ) : (
          <a
            href="/chat"
            className="inline-flex items-center justify-center gap-2 rounded border border-white bg-sky-400 px-5 py-3 font-mono text-sm font-bold text-black transition-colors hover:bg-sky-300"
          >
            <MessageSquare className="h-4 w-4" /> Try in chat
          </a>
        )}
        <a
          href="/docs"
          className="inline-flex items-center justify-center gap-2 rounded border border-white/15 bg-white/[0.03] px-5 py-3 font-mono text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white"
        >
          <BookOpen className="h-4 w-4" /> Gateway docs
        </a>
        {provider?.website ? (
          <a
            href={provider.website}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded border border-white/15 bg-white/[0.03] px-5 py-3 font-mono text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white"
          >
            {providerLabel} site
          </a>
        ) : null}
      </div>

      {isForecast && (
        <section className="mt-12 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold tracking-tight">Forecast API example</h2>
              <p className="mt-1 text-sm text-white/45">
                This is a time-series model — call <code className="rounded bg-white/10 px-1 text-xs">/api/forecast</code> with
                a history and a horizon. Paste extra columns as <code className="rounded bg-white/10 px-1 text-xs">past_covariates</code> for
                multivariate forecasting.
              </p>
            </div>
            <button
              type="button"
              onClick={copyMedia}
              className="inline-flex items-center justify-center gap-2 rounded border border-white/12 px-3 py-2 font-mono text-xs text-white/60 transition-colors hover:border-white/30 hover:text-white"
            >
              {mediaCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {mediaCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="mb-3 flex flex-wrap gap-2">
            {(['python', 'javascript', 'curl'] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setMediaLang(lang)}
                className={`rounded px-3 py-1.5 font-mono text-xs transition-colors ${mediaLang === lang ? 'bg-sky-400/15 text-sky-200' : 'border border-white/10 text-white/45 hover:text-white'}`}
              >
                {lang === 'javascript' ? 'JavaScript' : lang === 'python' ? 'Python' : 'cURL'}
              </button>
            ))}
          </div>
          <CodeBlock
            code={fcastSnippet}
            language={mediaLang === 'curl' ? 'bash' : mediaLang}
            preClassName="max-h-[460px] text-xs"
          />
          <a
            href="/time-series-forecasting"
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded border border-white bg-sky-400 px-4 py-2.5 font-mono text-sm font-bold text-black transition-colors hover:bg-sky-300"
          >
            <LineChart className="h-4 w-4" /> Try it in the forecaster
          </a>
        </section>
      )}

      {isVideo && (
        <section className="mt-12 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="grid gap-0 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
            <div className="border-b border-white/10 lg:border-b-0 lg:border-r">
              {videoDemo ? (
                <video src={videoDemo.outputUrl} controls muted loop playsInline className="aspect-video w-full bg-black object-contain" />
              ) : (
                <div className="flex aspect-video items-center justify-center bg-white/[0.03] text-sm font-mono text-white/35">
                  Generate a sample in the playground
                </div>
              )}
              {videoDemo && (
                <div className="grid gap-4 border-t border-white/10 p-4 md:grid-cols-[120px_minmax(0,1fr)]">
                  {videoDemo.imageUrl || videoDemo.imageUrls?.[0] ? (
                    <div>
                      <div className="mb-2 text-[10px] font-mono uppercase tracking-[0.16em] text-white/35">Input image</div>
                      <img
                        src={videoDemo.imageUrl || videoDemo.imageUrls?.[0]}
                        alt={`${name} reference input`}
                        className="aspect-square w-full rounded-lg border border-white/10 bg-white/[0.03] object-contain p-2"
                      />
                    </div>
                  ) : null}
                  <div className="min-w-0">
                    <div className="mb-2 text-[10px] font-mono uppercase tracking-[0.16em] text-white/35">Prompt</div>
                    <p className="text-sm leading-relaxed text-white/58">{videoDemo.prompt}</p>
                    <div className="mt-4 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-white/35">
                      <span className="rounded border border-white/10 px-2 py-1">{videoDemo.resolution}</span>
                      <span className="rounded border border-white/10 px-2 py-1">{videoDemo.duration}s</span>
                      <span className="rounded border border-white/10 px-2 py-1">{videoDemo.aspectRatio}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-5">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-bold tracking-tight">Video API example</h2>
                  <p className="mt-1 text-sm text-white/45">Copy the request or open it with this model selected in the playground.</p>
                </div>
                <button
                  type="button"
                  onClick={copyMedia}
                  className="inline-flex items-center justify-center gap-2 rounded border border-white/12 px-3 py-2 font-mono text-xs text-white/60 transition-colors hover:border-white/30 hover:text-white"
                >
                  {mediaCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {mediaCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="mb-3 flex flex-wrap gap-2">
                {(['python', 'javascript', 'curl'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setMediaLang(lang)}
                    className={`rounded px-3 py-1.5 font-mono text-xs transition-colors ${mediaLang === lang ? 'bg-sky-400/15 text-sky-200' : 'border border-white/10 text-white/45 hover:text-white'}`}
                  >
                    {lang === 'javascript' ? 'JavaScript' : lang === 'python' ? 'Python' : 'cURL'}
                  </button>
                ))}
              </div>
              <CodeBlock
                code={videoSnippet}
                language={mediaLang === 'curl' ? 'bash' : mediaLang}
                preClassName="max-h-[420px] text-xs"
              />
              <a
                href={`/playground?model=${encodeURIComponent(model.id)}&mode=video`}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded border border-white bg-sky-400 px-4 py-2.5 font-mono text-sm font-bold text-black transition-colors hover:bg-sky-300"
              >
                <Video className="h-4 w-4" /> Use this in playground
              </a>
            </div>
          </div>
        </section>
      )}

      {imageDemo && (
        <section className="mt-12 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="grid gap-0 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
            <div className="border-b border-white/10 lg:border-b-0 lg:border-r">
              <div className="grid grid-cols-2 gap-px bg-white/10">
                {imageDemo.imageUrl && (
                  <div className="bg-black">
                    <div className="border-b border-white/10 px-4 py-2 text-[10px] font-mono uppercase tracking-[0.16em] text-white/35">Input</div>
                    <img src={imageDemo.imageUrl} alt={`${name} input`} className="aspect-square w-full object-contain p-3" />
                  </div>
                )}
                <div className="bg-black">
                  <div className="border-b border-white/10 px-4 py-2 text-[10px] font-mono uppercase tracking-[0.16em] text-white/35">Output</div>
                  <img src={imageDemo.outputUrl} alt={`${name} output`} className="aspect-square w-full object-contain p-3" />
                </div>
              </div>
              <div className="border-t border-white/10 p-5">
                <h2 className="text-xl font-bold tracking-tight">{imageDemo.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{imageDemo.description}</p>
              </div>
            </div>

            <div className="p-5">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-bold tracking-tight">Image API example</h2>
                  <p className="mt-1 text-sm text-white/45">Copy the request or open it with this model selected in the playground.</p>
                </div>
                <button
                  type="button"
                  onClick={copyMedia}
                  className="inline-flex items-center justify-center gap-2 rounded border border-white/12 px-3 py-2 font-mono text-xs text-white/60 transition-colors hover:border-white/30 hover:text-white"
                >
                  {mediaCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {mediaCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="mb-3 flex flex-wrap gap-2">
                {(['python', 'javascript', 'curl'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setMediaLang(lang)}
                    className={`rounded px-3 py-1.5 font-mono text-xs transition-colors ${mediaLang === lang ? 'bg-sky-400/15 text-sky-200' : 'border border-white/10 text-white/45 hover:text-white'}`}
                  >
                    {lang === 'javascript' ? 'JavaScript' : lang === 'python' ? 'Python' : 'cURL'}
                  </button>
                ))}
              </div>
              <CodeBlock
                code={imageSnippet}
                language={mediaLang === 'curl' ? 'bash' : mediaLang}
                preClassName="max-h-[420px] text-xs"
              />
              <a
                href={`/playground?model=${encodeURIComponent(model.id)}&mode=image`}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded border border-white bg-sky-400 px-4 py-2.5 font-mono text-sm font-bold text-black transition-colors hover:bg-sky-300"
              >
                <ImageIcon className="h-4 w-4" /> Use this in playground
              </a>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};
